import * as m from '@staiter/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { CheckCircle2, FileText, Plus, XCircle } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  EmptyState,
  PageHeader,
  Pagination,
  type SortState,
  Table,
  type TableColumn,
} from '~/components/data';
import { PeriodCloseBanner } from '~/components/domain';
import { useToast } from '~/components/feedback';
import { Button, Select } from '~/components/primitives';
import { StatusBadge, type StatusBadgeStatus } from '~/components/status';
import { renderApiErrorMessage } from '~/lib/api/error-renderer';
import type { InvoiceListFilter, InvoiceListItem } from '~/lib/api/invoices';
import { bulkInvoiceAction, fetchArInvoices } from '~/lib/api/invoices';
import { formatDate, formatMoney } from '~/lib/format';
import { canDo, useUserScope } from '~/lib/rbac/use-user-scope';
import shared from './_auth._shared.module.css';

/**
 * S14-FE.3 — AR invoices list page.
 */

export const Route = createFileRoute('/_auth/invoices/ar')({
  staticData: { minMainWidth: 960 },
  component: InvoicesArPage,
});

// FE-P2-2 (QA 2026-06-04): invoice state → StatusBadge variant.
const INVOICE_STATE_BADGE: Record<string, StatusBadgeStatus> = {
  draft: 'needs_review',
  pending_approval: 'pending_review',
  posted: 'completed_draft',
  partially_paid: 'in_progress',
  paid: 'completed',
  cancelled: 'failed',
  void: 'failed',
};

// S-FIX-5-FE.4.2: column.id → BE sort enum mapping.
const SORT_COLUMN_MAP: Record<string, 'issue_date' | 'counterparty_name_ru' | 'total' | 'state'> = {
  issue: 'issue_date',
  cp: 'counterparty_name_ru',
  total: 'total',
  state: 'state',
};

function InvoicesArPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  // S-FIX-5-FE.13.1: pagination state.
  const [pagination, setPagination] = useState<{ limit: number; offset: number }>({
    limit: 50,
    offset: 0,
  });
  // S-FIX-5-FE.4.2: server-side sort state.
  const [sort, setSort] = useState<SortState>(null);
  const sortFilter = sort ? { sort: SORT_COLUMN_MAP[sort.id], sort_dir: sort.dir } : ({} as const);
  // FE-P2-1 (pass3 closure): state filter.
  const [stateFilter, setStateFilter] = useState<InvoiceListFilter['state'] | ''>('');
  // SET-AUDIT-7: selection state for bulk actions.
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // SET-AUDIT-7: bulk post/cancel.
  const bulkMut = useMutation({
    mutationFn: bulkInvoiceAction,
    onSuccess: (res, vars) => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      setSelected(new Set());
      const skippedCount = res.skipped.length;
      const skippedHint =
        skippedCount > 0
          ? ` Пропущено: ${skippedCount} (не draft / с платежами / отсутствуют)`
          : '';
      toast.push({
        variant: skippedCount === res.total ? 'warning' : 'success',
        title:
          vars.action === 'post'
            ? `Проведено: ${res.succeeded.length}/${res.total}`
            : `Отменено: ${res.succeeded.length}/${res.total}`,
        description: skippedHint || undefined,
      });
    },
    onError: (err) => {
      toast.push({
        variant: 'error',
        title: 'Массовая операция не выполнена',
        description: renderApiErrorMessage(err),
      });
    },
  });
  const query = useQuery({
    queryKey: [
      'invoices',
      'ar',
      pagination.limit,
      pagination.offset,
      sort?.id,
      sort?.dir,
      stateFilter,
    ],
    queryFn: () =>
      fetchArInvoices({
        ...pagination,
        ...sortFilter,
        ...(stateFilter ? { state: stateFilter } : {}),
      }),
  });

  const rows = query.data?.items ?? [];
  const totalCount =
    (query.data as { total_count?: number } | undefined)?.total_count ?? rows.length;
  // access-control (F9) — колонка компании только в holding-агрегате (>1 компании в выдаче).
  const entityNames = query.data?.entities ?? {};
  const showCompanyCol = new Set(rows.map((r) => r.entity_id)).size > 1;

  const columns: TableColumn<InvoiceListItem>[] = useMemo(
    () => [
      ...(showCompanyCol
        ? [
            {
              id: 'company',
              header: m.cp_field_company(),
              accessor: (r: InvoiceListItem) => entityNames[r.entity_id]?.name ?? '—',
              width: '150px',
            } satisfies TableColumn<InvoiceListItem>,
          ]
        : []),
      {
        id: 'doc',
        header: m.invoice_column_doc_number(),
        // FE-228 Pass 28: nowrap (voucher_no wraps на 3 строки в narrow viewport — «AP-/DRAFT-/001»).
        accessor: (r) => (
          <Link
            to="/invoices/$id"
            params={{ id: r.id }}
            className={shared.accentLink}
            style={{ whiteSpace: 'nowrap' }}
          >
            {r.document_number}
          </Link>
        ),
        mono: true,
        sticky: true,
        width: '160px',
      },
      {
        id: 'issue',
        header: m.invoice_column_issue_date(),
        accessor: (r) => formatDate(r.issue_date),
        width: '120px',
        sortable: true,
      },
      {
        id: 'due',
        header: m.invoice_column_due_date(),
        accessor: (r) => formatDate(r.due_date),
        width: '120px',
      },
      {
        id: 'cp',
        header: m.invoice_column_counterparty(),
        // S-FIX-5-FE.4.1: counterparty_name_ru/en denormalized BE.1.1.
        // Fallback на id8 если name undefined (старая BE-версия).
        accessor: (r) =>
          (r as { counterparty_name_ru?: string | null }).counterparty_name_ru ??
          (r as { counterparty_name_en?: string | null }).counterparty_name_en ??
          r.counterparty_id.slice(0, 8),
        // S-FIX-5-FE.4.2: server-side sortable via BE.1.4.
        sortable: true,
      },
      {
        id: 'total',
        header: m.invoice_column_total(),
        // FE-283 Pass 34: nowrap (money wraps на 2 строки «604 800,00 / KZT»).
        accessor: (r) => <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(r.total)}</span>,
        align: 'right',
        mono: true,
        sortable: true,
        width: '140px',
      },
      {
        id: 'outstanding',
        header: m.invoice_column_outstanding(),
        accessor: (r) => (
          <span style={{ whiteSpace: 'nowrap' }}>{formatMoney(r.outstanding_amount)}</span>
        ),
        align: 'right',
        mono: true,
        width: '140px',
      },
      {
        id: 'state',
        header: m.invoice_column_state(),
        accessor: (r) => (
          <StatusBadge status={INVOICE_STATE_BADGE[r.state] ?? 'needs_review'} size="sm" />
        ),
        width: '140px',
        sortable: true,
      },
    ],
    [showCompanyCol, entityNames],
  );

  const scope = useUserScope();
  const canCreate = canDo(scope, 'invoice', 'create');
  return (
    <div>
      <PageHeader
        title={m.phase2_invoices_ar_title()}
        actions={
          // F38 (WHS-F38): создание — только роли с правом `invoice:create`.
          canCreate ? (
            <Button
              variant="primary"
              leadingIcon={<Plus size={16} />}
              onClick={() =>
                void navigate({ to: '/invoices/new', search: { kind: 'ar' } as never })
              }
            >
              {m.invoice_button_new()}
            </Button>
          ) : undefined
        }
      />
      <PeriodCloseBanner scope="invoice" />
      {/* FE-P2-1 (pass3 closure): state filter row. */}
      <div className={shared.filters}>
        <Select
          label={m.invoice_column_state()}
          hideLabel
          size="sm"
          value={stateFilter}
          placeholder={m.invoice_column_state()}
          options={[
            { value: '', label: m.so_filter_all_statuses() },
            { value: 'draft', label: m.invoice_state_draft() },
            { value: 'posted', label: m.invoice_state_posted() },
            { value: 'partially_paid', label: m.invoice_state_partially_paid() },
            { value: 'paid', label: m.invoice_state_paid() },
            { value: 'cancelled', label: m.invoice_state_cancelled() },
          ]}
          onChange={(e) => {
            const v = e.target.value;
            setStateFilter(v === '' ? '' : (v as InvoiceListFilter['state']));
            setPagination((p) => ({ ...p, offset: 0 }));
          }}
        />
      </div>
      {rows.length === 0 && !query.isLoading ? (
        <EmptyState title={m.invoice_empty_title()} icon={<FileText size={32} />} />
      ) : (
        <>
          <Table<InvoiceListItem>
            data={rows}
            columns={columns}
            rowKey={(r) => r.id}
            ariaLabel={m.phase2_invoices_ar_title()}
            loading={query.isLoading}
            emptyTitle={m.invoice_empty_title()}
            controlledSort={sort}
            onSortChange={(next) => {
              setSort(next);
              setPagination((p) => ({ ...p, offset: 0 }));
            }}
            stickyFirstColumn
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
          />
          <Pagination
            totalCount={totalCount}
            limit={pagination.limit}
            offset={pagination.offset}
            onChange={setPagination}
          />
        </>
      )}
      {/* SET-AUDIT-7: sticky bulk action bar при ≥1 selected. */}
      {selected.size > 0 && (
        <div
          role="toolbar"
          aria-label="Массовые действия"
          style={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: '10px 16px',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            zIndex: 100,
          }}
        >
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {m.ui_vybrano()} <strong>{selected.size}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<CheckCircle2 size={14} aria-hidden="true" />}
            onClick={() => bulkMut.mutate({ ids: Array.from(selected), action: 'post' })}
            disabled={bulkMut.isPending}
          >
            {m.ui_provesti()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leadingIcon={<XCircle size={14} aria-hidden="true" />}
            onClick={() => bulkMut.mutate({ ids: Array.from(selected), action: 'cancel' })}
            disabled={bulkMut.isPending}
          >
            {m.ui_otmenit()}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
            {m.ui_snyat_vydelenie()}
          </Button>
        </div>
      )}
    </div>
  );
}
